import { breakpoints } from '../styles';
import React from 'react';
import styled from 'styled-components';

interface GridColumn<T> {
    key: keyof T;
    label: string;
    renderCell?: (value: any, row: T) => React.ReactNode;
    width?: string | number;
    hideOnMobile?: boolean;
    textAlign?: 'left' | 'center' | 'right'; // Add this new property
}

interface GridWrapperProps<T extends object> {
    columns: GridColumn<T>[];
    data: T[];
    className?: string;
}

const TableContainer = styled.div`
    max-height: calc(100vh - 300px); // Adjust height as needed
    overflow: auto;
    position: relative;
    ${breakpoints.onlyMobile} {
        max-height: unset; // Full height on mobile
        margin-bottom: 100px;
    }
`;

const Table = styled.table`
    width: 100%;
    border-collapse: separate;
    border-spacing: 0 6px; // breathable row space
    table-layout: fixed;
    font-size: 14px; // Override global font size for this table
    margin: 0;
    ${breakpoints.onlyMobile} {
        font-size: 12px;
    }
`;

const Thead = styled.thead`
    background-color: #f0f0f0;
`;

const Th = styled.th<{
    hideOnMobile?: boolean;
    isLastColumn?: boolean;
    textAlign?: string;
}>`
    padding: 8px;
    position: sticky;
    top: -1px;
    background: #f2f2f2;
    z-index: 2;
    text-align: ${(props) =>
        props.textAlign
            ? props.textAlign
            : props.isLastColumn
            ? 'right'
            : 'left'};
    border-bottom: 1px solid #d8d8d8;
    border-top: 1px solid #d8d8d8;
    &:first-child {
        border-left: 1px solid #d8d8d8;
    }
    &:last-child {
        border-right: 1px solid #d8d8d8;
    }
    ${(props) =>
        props.hideOnMobile &&
        `
        ${breakpoints.onlyMobile} {
            display: none;
        }
    `}
    &::after {
        height: 32px;
        content: '';
        position: absolute;
        bottom: 0;
        left: 0;
        width: 100%;
        z-index: 1;
    }
    ${breakpoints.onlyMobile} {
        &:nth-child(2) {
            border-left: 1px solid #d8d8d8;
        }
        padding: 7px 6px;
    }
`;

const Tr = styled.tr`
    // &:nth-child(even) {
    //     background: #f8f8f8;
    // }
    // &:last-child td {
    //     border-bottom: none;
    // }
`;

const Td = styled.td<{
    hideOnMobile?: boolean;
    isLastColumn?: boolean;
    textAlign?: string;
}>`
    padding: 8px;
    word-wrap: break-word;
    text-align: ${(props) =>
        props.textAlign
            ? props.textAlign
            : props.isLastColumn
            ? 'right'
            : 'inherit'};
    border-top: 1px solid #d8d8d8;
    border-bottom: 1px solid #d8d8d8;
    /* Keep minimal look, but add side borders only */
    &:first-child {
        text-align: center;
        border-right: none;
        border-left: 1px solid #d8d8d8;
    }
    &:last-child {
        border-right: 1px solid #d8d8d8;
        border-left: none;
    }
    ${(props) =>
        props.hideOnMobile &&
        `
        ${breakpoints.onlyMobile} {
            display: none;
        }
    `}
    ${breakpoints.onlyMobile} {
        &:nth-child(2) {
            border-left: 1px solid #d8d8d8;
        }
        padding: 7px 6px;
    }
`;

function GridWrapper<T extends object>({
    columns,
    data,
    className,
}: GridWrapperProps<T>) {
    return (
        <TableContainer>
            <Table className={className}>
                <Thead>
                    <tr>
                        {columns.map((col, colIndex) => (
                            <Th
                                key={String(col.key)}
                                style={
                                    col.width ? { width: col.width } : undefined
                                }
                                hideOnMobile={col.hideOnMobile}
                                isLastColumn={colIndex === columns.length - 1}
                                textAlign={col.textAlign}
                            >
                                {col.label}
                            </Th>
                        ))}
                    </tr>
                </Thead>
                <tbody>
                    {data.map((row, rowIndex) => (
                        <Tr key={rowIndex}>
                            {columns.map((col, colIndex) => (
                                <Td
                                    key={String(col.key)}
                                    style={
                                        col.width
                                            ? { width: col.width }
                                            : undefined
                                    }
                                    hideOnMobile={col.hideOnMobile}
                                    isLastColumn={
                                        colIndex === columns.length - 1
                                    }
                                    textAlign={col.textAlign}
                                >
                                    {col.renderCell
                                        ? col.renderCell(row[col.key], row)
                                        : (row[col.key] as React.ReactNode)}
                                </Td>
                            ))}
                        </Tr>
                    ))}
                </tbody>
            </Table>
        </TableContainer>
    );
}

export default GridWrapper;